import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { onAuthFailure, waitForPendingRefresh } from '../api/apiClient';
import { authApi } from './authApi';
import { tokenService } from './tokenService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const initializationStarted = useRef(false);
  const logoutRequest = useRef(null);

  const clearAuthentication = useCallback(() => {
    tokenService.clearAccessToken();
    setUser(null);
  }, []);

  const loadCurrentUser = useCallback(async (generation = tokenService.getSessionGeneration()) => {
    const currentUser = await authApi.getCurrentUser();
    if (generation !== tokenService.getSessionGeneration() || !tokenService.canRefresh()) return null;
    setUser(currentUser);
    return currentUser;
  }, []);

  const initializeAuthentication = useCallback(async () => {
    const generation = tokenService.beginSession();
    try {
      await authApi.ensureCsrfCookie();
      if (generation !== tokenService.getSessionGeneration()) return null;
      const { access } = await authApi.refresh();
      if (!tokenService.setAccessToken(access, generation)) return null;
      return await loadCurrentUser(generation);
    } catch (error) {
      if (generation === tokenService.getSessionGeneration()) clearAuthentication();
      return null;
    } finally {
      setIsAuthReady(true);
    }
  }, [clearAuthentication, loadCurrentUser]);

  useEffect(() => {
    if (initializationStarted.current) {
      return;
    }
    initializationStarted.current = true;
    initializeAuthentication();
  }, [initializeAuthentication]);

  useEffect(() => onAuthFailure(clearAuthentication), [clearAuthentication]);

  const login = useCallback(async ({ username, password }) => {
    // Wait for logout's cookie deletion before allowing a new login cookie.
    if (logoutRequest.current) await logoutRequest.current;
    const generation = tokenService.beginSession();
    setUser(null);
    try {
      await waitForPendingRefresh();
      if (generation !== tokenService.getSessionGeneration()) throw new Error('Authentication session has changed.');
      await authApi.ensureCsrfCookie();
      if (generation !== tokenService.getSessionGeneration()) throw new Error('Authentication session has changed.');
      const { access } = await authApi.login({ username, password });
      if (!tokenService.setAccessToken(access, generation)) throw new Error('Authentication session has changed.');
      const currentUser = await loadCurrentUser(generation);
      if (!currentUser) throw new Error('Authentication session has changed.');
      setPasswordChanged(false);
      return currentUser;
    } catch (error) {
      if (generation === tokenService.getSessionGeneration()) clearAuthentication();
      throw error;
    }
  }, [clearAuthentication, loadCurrentUser]);

  const logout = useCallback(() => {
    if (logoutRequest.current) return logoutRequest.current;
    clearAuthentication();
    const pending = (async () => {
      let serverRevocationConfirmed = false;
      try {
        await authApi.ensureCsrfCookie();
        await authApi.logout();
        serverRevocationConfirmed = true;
      } catch (error) {
        // Local state is already cleared even if cookie revocation fails.
      }
      return { serverRevocationConfirmed };
    })().finally(() => {
      if (logoutRequest.current === pending) logoutRequest.current = null;
    });
    logoutRequest.current = pending;
    return pending;
  }, [clearAuthentication]);

  const completePasswordChange = useCallback((generation) => {
    // A delayed successful change may arrive after another request signed us out.
    // Do not let it sign out a newer, explicitly started login session.
    if (generation !== tokenService.getSessionGeneration() && tokenService.canRefresh()) return false;
    setPasswordChanged(true);
    clearAuthentication();
    return true;
  }, [clearAuthentication]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user && tokenService.getAccessToken()),
      isAuthReady,
      login,
      logout,
      refreshUser: loadCurrentUser,
      completePasswordChange,
      passwordChanged,
    }),
    [isAuthReady, loadCurrentUser, login, logout, user, completePasswordChange, passwordChanged]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
